/**
 * Persistência mínima da sessão do simulador — Item 275.
 *
 * Mock harness v1 — escopo aprovado: token JWT short-lived + sessionId +
 * expiresAt + conversationId em localStorage. Histórico mensagens NÃO
 * persistido (fora do escopo; o cliente Webchat de produção resolverá).
 *
 * Grace canonical de 30s antes do expiresAt para evitar reaproveitar token
 * que expira em pleno handshake WS.
 */

import type { MockSessionResponse } from './types';

const KEY_TOKEN = 'lume-simulador-session-token';
const KEY_SESSION_ID = 'lume-simulador-session-id';
const KEY_EXPIRES_AT = 'lume-simulador-session-expires-at';
const KEY_CONVERSATION_ID = 'lume-simulador-active-conversation-id';
const KEY_CHANNEL_IDENTIFIER = 'lume-simulador-active-channel-identifier';
const EXPIRY_GRACE_MS = 30_000;

/** `storage` é injetável para o spec rodar em Node sem jsdom. Default: o localStorage do browser. */
function defaultStorage(): Storage {
  return window.localStorage;
}

export interface StoredSession {
  accessToken: string;
  sessionId: string;
  expiresAt: number;
}

export function loadStoredSession(storage: Storage = defaultStorage()): StoredSession | null {
  try {
    const token = storage.getItem(KEY_TOKEN);
    const sessionId = storage.getItem(KEY_SESSION_ID);
    const expiresAtStr = storage.getItem(KEY_EXPIRES_AT);
    if (token === null || sessionId === null || expiresAtStr === null) return null;
    const expiresAt = Number.parseInt(expiresAtStr, 10);
    if (Number.isNaN(expiresAt)) return null;
    if (Date.now() + EXPIRY_GRACE_MS >= expiresAt) return null;
    return { accessToken: token, sessionId, expiresAt };
  } catch {
    return null;
  }
}

export function saveSession(response: MockSessionResponse): StoredSession {
  const expiresAt = Date.now() + response.expiresIn * 1000;
  window.localStorage.setItem(KEY_TOKEN, response.accessToken);
  window.localStorage.setItem(KEY_SESSION_ID, response.sessionId);
  window.localStorage.setItem(KEY_EXPIRES_AT, String(expiresAt));
  return { accessToken: response.accessToken, sessionId: response.sessionId, expiresAt };
}

export function clearSession(storage: Storage = defaultStorage()): void {
  storage.removeItem(KEY_TOKEN);
  storage.removeItem(KEY_SESSION_ID);
  storage.removeItem(KEY_EXPIRES_AT);
}

export function loadStoredConversationId(storage: Storage = defaultStorage()): string | null {
  try {
    return storage.getItem(KEY_CONVERSATION_ID);
  } catch {
    return null;
  }
}

/**
 * Salva a conversa ativa JUNTO do identificador de canal a que ela pertence. Sem o par, a
 * conversa salva vale para qualquer `?cid=` — e foi assim que a C2 da noturna de 04/Set/2026
 * reabriu a conversa da C1 (A-21).
 */
export function saveConversationId(
  conversationId: string,
  channelIdentifier: string,
  storage: Storage = defaultStorage(),
): void {
  storage.setItem(KEY_CONVERSATION_ID, conversationId);
  storage.setItem(KEY_CHANNEL_IDENTIFIER, channelIdentifier);
}

export function clearConversationId(storage: Storage = defaultStorage()): void {
  storage.removeItem(KEY_CONVERSATION_ID);
  storage.removeItem(KEY_CHANNEL_IDENTIFIER);
}

/**
 * Antes de restaurar qualquer coisa do localStorage, confere se o que está salvo é do MESMO
 * identificador de canal da URL atual. Se não é (ou se não dá para provar — versões anteriores
 * não gravavam o cid), descarta sessão e conversa: cenário novo nasce limpo. É a regra do §12
 * do CLAUDE.md do Simulador («cenário reaproveitado herda o estado do anterior») virando código.
 */
export function reconcileStoredScenario(
  currentChannelIdentifier: string,
  storage: Storage = defaultStorage(),
): { reset: boolean; previousChannelIdentifier: string | null } {
  let previous: string | null = null;
  let hasConversation = false;
  try {
    previous = storage.getItem(KEY_CHANNEL_IDENTIFIER);
    hasConversation = storage.getItem(KEY_CONVERSATION_ID) !== null;
  } catch {
    return { reset: false, previousChannelIdentifier: null };
  }
  const sameWorld = previous !== null && previous === currentChannelIdentifier;
  if (sameWorld || (!hasConversation && previous === null)) {
    return { reset: false, previousChannelIdentifier: previous };
  }
  clearSession(storage);
  clearConversationId(storage);
  return { reset: true, previousChannelIdentifier: previous };
}
